/**
 * Sticky landing header (56px on phones, 64px from md up).
 *
 * Mobile: logo left, "Sign in" text link + menu toggle right. The menu opens as a
 * panel directly under the header, announces state with aria-expanded /
 * aria-controls, closes on Escape, on any navigation, and on outside click,
 * and returns focus to the toggle when closed.
 *
 * Desktop (lg+): inline anchor nav plus Sign in / Create account.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../Logo";
import { Icon } from "./icons";
import { NAV_ITEMS } from "./content";

export function LandingHeader() {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Escape closes; outside click closes; focus first link when opened.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (
        target &&
        !panelRef.current?.contains(target) &&
        !toggleRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    panelRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  // Close if the viewport grows past the mobile breakpoint while open.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:px-6 md:h-16">
        <Link
          to="/"
          className="inline-flex min-h-[44px] items-center rounded-md"
          aria-label="StampmatS home"
          onClick={close}
        >
          <Logo className="h-8 md:h-9" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="inline-flex min-h-[44px] items-center rounded-md px-3 text-sm font-medium text-ink-soft transition-colors hover:text-aigentico-navy"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/signin"
            className="inline-flex min-h-[44px] items-center rounded-md px-3 text-sm font-medium text-ink-soft transition-colors hover:text-aigentico-navy"
          >
            Sign in
          </Link>
          <Link to="/signup" className="btn-primary hidden min-h-[44px] px-4 py-2 text-xs sm:inline-flex">
            Create account
          </Link>
          <button
            ref={toggleRef}
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-aigentico-navy transition-colors hover:bg-bg-soft lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? "close" : "menu"} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile panel — rendered in the DOM only while open so hidden links
          are not tab stops. */}
      {open && (
        <div
          ref={panelRef}
          id={menuId}
          className="absolute inset-x-0 top-full border-b border-border bg-white shadow-[0_12px_32px_-16px_rgba(0,20,100,0.25)] lg:hidden"
        >
          <nav className="mx-auto flex max-w-6xl flex-col px-4 py-3 sm:px-6" aria-label="Primary mobile">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={close}
                className="flex min-h-[48px] items-center justify-between rounded-md px-2 text-base font-medium text-ink transition-colors hover:bg-bg-soft hover:text-aigentico-navy"
              >
                {item.label}
                <Icon name="arrowRight" size={18} className="text-ink-faint" />
              </a>
            ))}
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-4">
              <Link to="/signin" onClick={close} className="btn-secondary min-h-[48px] w-full text-xs">
                Sign in
              </Link>
              <Link to="/signup" onClick={close} className="btn-primary min-h-[48px] w-full text-xs">
                Create account
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
